import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { toast } from "react-toastify";
import {
  // Actions
  initializeFormData,
  updateFormField,
  resetForm,
  // API Thunks
  updateStoreOpenStatus,
  updateStoreLabels,
  updateStorePhoneNumber,
  updateStoreWorkingTime,updateStoreTitle,
  transferLocation,
  fetchAllStoreAccounts,
  // Selectors
  selectFormData,
  selectIsLoading,
  selectHasChanges, // Changed from selectIsFormChanged
  selectUpdateStatus,
  selectAllStoreAccounts,
  selectGMBProfile,
} from "../../../Store/Gmb/gmbProfileSlice";

import { setEditing } from "../../../Store/Gmb/gmbStoreSlice";

export const useGMBProfile = () => {
  const dispatch = useDispatch();

  // Get necessary state from Redux
  const formData = useSelector(selectFormData);
  const updateStatus = useSelector(selectUpdateStatus);
  const allStoreAccounts = useSelector(selectAllStoreAccounts);
  const hasChanges = useSelector(selectHasChanges);
  const { error } = useSelector(selectGMBProfile); // Get error from profile state
  const storeDetail = useSelector((state) => state.gmbStore.storeDetail);
  const isEditing = useSelector((state) => state.gmbStore.isEditing);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const [openingTime, closingTime] = storeDetail.regular_hours
    ? storeDetail?.regular_hours.match(/\d{1,2}:\d{2}/g)?.map((time) => {
        const [hour, minute] = time?.split(":")?.map(Number);
        return `${hour.toString().padStart(2, "0")}:${minute.toString().padStart(2, "0")}`;
      })
    : [
        formData.operatingHours?.openingTime
          ? formData.operatingHours.openingTime.padStart(5, "0")
          : "00:00",
        formData.operatingHours?.closingTime
          ? formData.operatingHours.closingTime.padStart(5, "0")
          : "00:00",
      ];

  // Rest of your hook implementation remains the same
  const isStoreStatusPending =
    storeDetail?.is_verified === "COMPLETED" &&
    !storeDetail?.has_voice_of_merchant;

  const IS_STORE_CLOSED_PERMANENTLY =
    formData.storeStatus === "CLOSED_PERMANENTLY";

  useEffect(() => {
    if (storeDetail) {
      dispatch(initializeFormData(storeDetail));
     //call this when account move feature is implemented
      // dispatch(fetchAllStoreAccounts());
    }
  }, [dispatch, storeDetail]);

  const handleFieldUpdate = (field, value) => {
    dispatch(updateFormField({ field, value }));
  };

  const handleEditToggle = (editing) => {
    //check if "is_verified": "COMPLETED", and if "has_voice_of_merchant": false
    //send toast message
    const isStoreStatusPending =
      storeDetail?.is_verified === "COMPLETED" &&
      !storeDetail?.has_voice_of_merchant;
    if (isStoreStatusPending) {
      toast.error("You cannot edit this store until it is verified!");
      return;
    }
    dispatch(setEditing(editing));
  };

  const handleSubmit = async () => {
    if (!hasChanges) return false;
    setIsSubmitting(true);

    try {
      const updatePromises = [];

      if(formData?.title && formData?.title !== storeDetail?.title){
        updatePromises.push(
          dispatch(
            updateStoreTitle({
              account_id: storeDetail.account_id,
              location_id: storeDetail.location_id,
              new_title: formData?.title,
            }),
          ).unwrap(),
        );
      }

      if (formData.storeStatus !== storeDetail.open_status) {
        updatePromises.push(
          dispatch(
            updateStoreOpenStatus({
              account_id: storeDetail.account_id,
              location_id: storeDetail.location_id,
              new_open_status: formData.storeStatus,
            }),
          ).unwrap(),
        );
      }

      if (formData.storeLabels !== storeDetail.labels) {
        updatePromises.push(
          dispatch(
            updateStoreLabels({
              account_id: storeDetail.account_id,
              location_id: storeDetail.location_id,
              new_labels: formData.storeLabels,
            }),
          ).unwrap(),
        );
      }
      const storePrimaryPhone = storeDetail.primary_phone?.replace(/[^\d]|^0+/g, "") || "";
      if (formData.phoneNumber !== storePrimaryPhone) {
        updatePromises.push(
          dispatch(
            updateStorePhoneNumber({
              account_id: storeDetail.account_id,
              location_id: storeDetail.location_id,
              new_phone_number: formData.phoneNumber,
            }),
          ).unwrap(),
        );
      }

      if (formData.currentAccountName !== storeDetail.account_name) {
        const newAccount = allStoreAccounts?.find(
          (acc) =>
            acc.account_name.toLowerCase() ===
            formData.currentAccountName.toLowerCase(),
        );
        if (newAccount) {
          updatePromises.push(
            dispatch(
              transferLocation({
                new_account_id: newAccount.account_id,
                location_id: storeDetail.location_id,
              }),
            ).unwrap(),
          );
        }
      }

      if (
        formData.operatingHours?.openingTime !== openingTime ||
        formData.operatingHours?.closingTime !== closingTime
      ) {
        updatePromises.push(
          dispatch(
            updateStoreWorkingTime({
              account_id: storeDetail.account_id,
              location_id: storeDetail.location_id,
              new_working_time: {
                openingTime: formData.operatingHours.openingTime,
                closingTime: formData.operatingHours.closingTime,
              },
            }),
          ).unwrap(),
        );
      }

      if (updatePromises.length > 0) {
        await Promise.all(updatePromises);
        setIsSubmitting(false);
        handleEditToggle(false);

        return true;
      }
      return false;
    } catch (error) {
      setIsSubmitting(false);
      console.error("Error while saving changes", error);
      if(error.includes("401")){
        toast.error("Unauthorized User");
      }else{
      toast.error("Failed to save changes");
      }
      return false;
    }
  };

  const handleCancel = () => {
    dispatch(resetForm());
    handleEditToggle(false);
  };

  const statusOptions = ["OPEN", "CLOSED_PERMANENTLY", "CLOSED_TEMPORARILY"];
  const accountNameOptions = allStoreAccounts?.map((item) => item.account_name);

  return {
    // State
    formData,
    isSubmitting,
    error,
    updateStatus,
    hasChanges,
    isEditing,
    isStoreStatusPending,
    IS_STORE_CLOSED_PERMANENTLY,
    storeDetail,

    // Options
    statusOptions,
    accountNameOptions,
    allStoreAccounts,

    // Handlers
    handleFieldUpdate,
    handleSubmit,
    handleCancel,
    handleEditToggle,
  };
};
